// ** UI
import useScrollTrigger from '@mui/material/useScrollTrigger';
import MuiAppBar, { type AppBarProps } from '@mui/material/AppBar';
import MuiToolbar, { type ToolbarProps } from '@mui/material/Toolbar';
import { useTheme, styled } from '@mui/material/styles';

// ** Types
import type { User } from 'next-auth';
import type { LayoutProps } from '@/types/layoutProps';

type VerticalLayout = NonNullable<LayoutProps['verticalLayoutProps']>;
type AppBar = NonNullable<VerticalLayout['appBar']>;
type Props = {
	path?: string;
	hidden?: LayoutProps['hidden'];
	toggleNavVisibility: () => void;
	settings: LayoutProps['settings'];
	saveSettings: LayoutProps['saveSettings'];
	appBarContent: AppBar['content'];
	appBarProps: AppBar['componentProps'];
	currentUser: User;
};

const AppBar = styled(MuiAppBar)<AppBarProps>(({ theme }) => ({
	transition: 'none',
	alignItems: 'center',
	justifyContent: 'center',
	backgroundColor: theme.palette.background.paper,
	color: theme.palette.text.primary,
	minHeight: theme.mixins.toolbar.minHeight,
	backdropFilter: 'blur(8px)',
	// backgroundColor: hexToRGBA(theme.palette.background.default, 0.85),
	height: 72
}));

const Toolbar = styled(MuiToolbar)<ToolbarProps>(({ theme }) => ({
	width: '100%',
	// borderBottomLeftRadius: 10,
	// borderBottomRightRadius: 10,
	padding: `${theme.spacing(4)} !important`,
	minHeight: `${theme.mixins.toolbar.minHeight}px !important`,
	transition: 'padding .25s ease-in-out, box-shadow .25s ease-in-out, backdrop-filter .25s ease-in-out'
}));

const CustomAppBar = ({ settings, appBarProps, appBarContent: userAppBarContent, ...props }: Props) => {
	// Hooks
	const theme = useTheme();
	const scrollTrigger = useScrollTrigger({
		threshold: 0,
		disableHysteresis: true
	});

	// Vars
	const { skin, appBar, contentWidth } = settings;

	const appBarFixedStyles = () => {
		return {
			px: `${theme.spacing(5)} !important`,
			// boxShadow: skin === 'bordered' ? 0 : 3,
			// ...(appBarBlur && { backdropFilter: 'blur(8px)' }),
			// backgroundColor: hexToRGBA(
			// 	theme.palette.background.default,
			// 	appBarBlur ? 0.85 : 1
			// ),
			...(skin === 'bordered' && {
				border: `1px solid ${theme.palette.divider}`,
				borderTopWidth: 0
			})
		};
	};

	if (appBar === 'hidden') {
		return null;
	}

	let userAppBarStyle = {};
	if (appBarProps?.sx) {
		userAppBarStyle = appBarProps.sx;
	}
	const userAppBarProps = Object.assign({}, appBarProps);
	delete userAppBarProps.sx;

	return (
		<AppBar
			elevation={0}
			color="default"
			sx={{ ...userAppBarStyle }}
			position={appBar === 'fixed' ? 'sticky' : 'static'}
			{...userAppBarProps}
		>
			<Toolbar
				sx={{
					...(appBar === 'fixed' && scrollTrigger && { ...appBarFixedStyles() }),
					boxShadow: skin === 'bordered' ? 0 : 3,
					...(contentWidth === 'boxed' && {
						'@media (min-width:1440px)': {
							maxWidth: `calc(1440px - ${theme.spacing(6)} * 2)`
						}
					})
				}}
			>
				{userAppBarContent?.(props) || null}
			</Toolbar>
		</AppBar>
	);
};

export default CustomAppBar;
